import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Truck, ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { EMIRATE_NAMES } from "@/data/translations";
import { formatAED } from "@/data/products";
import { api } from "@/lib/api";

const inputCls =
  "w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm text-brandink outline-none transition-colors duration-200 placeholder:text-muted/70 focus:border-link";

export default function CheckoutModal() {
  const { checkoutOpen, setCheckoutOpen, detailed, subtotal, clearCart } = useCart();
  const { customer, authHeaders } = useAuth();
  const { t, lang } = useLanguage();
  const [step, setStep] = useState(1);
  const [rates, setRates] = useState(null);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [method, setMethod] = useState("card");
  const [orderResult, setOrderResult] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", emirate: localStorage.getItem("cambroo_emirate") || "Dubai" });

  useEffect(() => {
    api.get("/shipping/rates").then((data) => setRates(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f,
        name: customer.name || f.name,
        email: customer.email || f.email,
        phone: customer.phone || f.phone,
      }));
    }
  }, [customer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const detailsValid = form.name && /\S+@\S+\.\S+/.test(form.email) && form.phone && form.address;

  const fee = rates
    ? subtotal >= rates.free_threshold
      ? 0
      : rates.rates[form.emirate] ?? 30
    : 0;
  const total = subtotal + fee;

  const close = () => {
    if (redirecting) return;
    setCheckoutOpen(false);
    setTimeout(() => {
      setStep(1);
      setError(null);
      setMethod("card");
      setOrderResult(null);
    }, 300);
  };

  const pay = async () => {
    setRedirecting(true);
    setError(null);
    const items = detailed.map((i) => ({ id: i.id, qty: i.qty }));
    try {
      if (method === "card") {
        const data = await api.post("/payments/cart-checkout", {
          items,
          customer: form,
          origin_url: window.location.origin,
        }, authHeaders());
        window.location.href = data.checkout_url;
        return;
      }
      const data = await api.post("/orders/offline", { items, customer: form, method }, authHeaders());
      setOrderResult(data);
      clearCart();
      setStep(3);
      setRedirecting(false);
    } catch (e) {
      setError(t("co_error"));
      setRedirecting(false);
    }
  };

  const METHODS = [
    { id: "card", label: t("pm_card") },
    { id: "cod", label: t("pm_cod") },
    { id: "bank_transfer", label: t("pm_bank") },
  ];

  const payLabel =
    method === "card" ? t("co_pay") : method === "cod" ? t("co_place_cod") : t("co_place_bank");

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <>
          <motion.div
            data-testid="checkout-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[85] bg-black/50"
          />
          <motion.div
            data-testid="checkout-modal"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[90] m-auto h-fit max-h-[90vh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">{t("co_label")}</p>
                <h3 className="mt-0.5 text-lg font-bold text-brandink">
                  {step === 3 ? t("os_title") : t("co_title")}
                </h3>
              </div>
              <button
                data-testid="checkout-close-button"
                onClick={close}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-brandink transition-colors hover:bg-page"
                aria-label="Close checkout"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 py-5">
              {!customer && (
                <div data-testid="checkout-auth-required" className="flex flex-col items-center gap-4 py-10 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand text-brandink">
                    <ShieldCheck className="h-6 w-6" />
                  </span>
                  <h4 className="text-lg font-bold text-brandink">{t("acc_required_title")}</h4>
                  <p className="max-w-xs text-sm leading-relaxed text-muted">{t("acc_required_sub")}</p>
                  <a
                    href="/account"
                    data-testid="checkout-signin-button"
                    className="mt-1 rounded-md bg-brand px-8 py-3 text-sm font-bold text-brandink transition-colors duration-200 hover:bg-branddark"
                  >
                    {t("acc_signin_cta")}
                  </a>
                </div>
              )}
              {customer && step === 1 && (
                <div data-testid="checkout-step-details">
                  <p className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
                    <Truck className="h-4 w-4 text-link" /> {t("co_step1")}
                  </p>
                  <div className="space-y-3">
                    <input data-testid="checkout-name-input" className={inputCls} placeholder={t("co_name")} value={form.name} onChange={set("name")} />
                    <input data-testid="checkout-email-input" className={inputCls} placeholder={t("co_email")} type="email" value={form.email} onChange={set("email")} />
                    <input data-testid="checkout-phone-input" className={inputCls} placeholder={t("co_phone")} value={form.phone} onChange={set("phone")} />
                    <input data-testid="checkout-address-input" className={inputCls} placeholder={t("co_address")} value={form.address} onChange={set("address")} />
                    <select
                      data-testid="checkout-emirate-select"
                      className={`${inputCls} cursor-pointer`}
                      value={form.emirate}
                      onChange={set("emirate")}
                    >
                      {(rates?.emirates || []).map((e) => (
                        <option key={e} value={e}>
                          {EMIRATE_NAMES[lang][e] || e}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-4 space-y-1.5 rounded-lg bg-page p-4 text-sm">
                    <div className="flex justify-between text-muted">
                      <span>{t("co_subtotal")}</span>
                      <span data-testid="checkout-subtotal">{formatAED(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-muted">
                      <span>{t("co_shipping")}</span>
                      <span data-testid="checkout-shipping-fee" className={fee === 0 ? "font-semibold text-success" : ""}>
                        {fee === 0 ? t("co_free") : formatAED(fee)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-line pt-2 text-base font-extrabold text-brandink">
                      <span>{t("co_total")}</span>
                      <span data-testid="checkout-total">{formatAED(total)}</span>
                    </div>
                  </div>
                  <button
                    data-testid="checkout-continue-button"
                    disabled={!detailsValid}
                    onClick={() => setStep(2)}
                    className="mt-4 w-full rounded-md bg-brand py-3 text-sm font-bold text-brandink transition-colors duration-200 hover:bg-branddark disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t("co_continue")} — {formatAED(total)}
                  </button>
                </div>
              )}

              {customer && step === 2 && (
                <div data-testid="checkout-step-payment">
                  <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
                    <ShieldCheck className="h-4 w-4 text-link" /> {t("co_step2")}
                  </p>

                  <p className="mb-2 text-xs font-bold text-brandink">{t("pm_label")}</p>
                  <div className="mb-5 grid grid-cols-3 gap-2" data-testid="payment-methods">
                    {METHODS.map((m) => (
                      <button
                        key={m.id}
                        data-testid={`pay-method-${m.id}`}
                        onClick={() => setMethod(m.id)}
                        className={`rounded-md border px-2 py-2.5 text-center text-[11px] font-semibold leading-tight transition-colors duration-200 ${
                          method === m.id
                            ? "border-brand bg-[#FFFBE0] text-brandink"
                            : "border-line bg-white text-muted hover:border-brandink/40 hover:text-brandink"
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>

                  <ul className="mb-4 space-y-2 border-b border-line pb-4">
                    {detailed.map((i) => (
                      <li key={i.id} className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-brandink/90">
                          {i.name} <span className="text-muted">× {i.qty}</span>
                        </span>
                        <span className="font-medium text-brandink">{formatAED(i.price * i.qty)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between text-muted">
                      <span>{t("co_subtotal")}</span>
                      <span>{formatAED(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-muted">
                      <span>
                        {t("co_shipping")} · {EMIRATE_NAMES[lang][form.emirate] || form.emirate}
                      </span>
                      <span className={fee === 0 ? "font-semibold text-success" : ""}>{fee === 0 ? t("co_free") : formatAED(fee)}</span>
                    </div>
                    <div className="flex justify-between border-t border-line pt-2 text-base font-extrabold text-brandink">
                      <span>{t("co_total")}</span>
                      <span data-testid="checkout-review-total">{formatAED(total)}</span>
                    </div>
                  </div>
                  {error && (
                    <p data-testid="checkout-error" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-600">{error}</p>
                  )}
                  <p className="mt-4 rounded-md border border-brand/50 bg-[#FFFBE0] px-4 py-2.5 text-[11px] leading-relaxed text-brandink">
                    {t("co_secure")}
                  </p>
                  <div className="mt-5 flex gap-3">
                    <button
                      data-testid="checkout-back-button"
                      onClick={() => setStep(1)}
                      disabled={redirecting}
                      className="rounded-md border border-line px-5 py-3 text-sm font-semibold text-brandink transition-colors hover:bg-page disabled:opacity-40"
                    >
                      {t("co_back")}
                    </button>
                    <button
                      data-testid="checkout-pay-button"
                      disabled={redirecting}
                      onClick={pay}
                      className="flex flex-1 items-center justify-center gap-2 rounded-md bg-brand py-3 text-sm font-bold text-brandink transition-colors duration-200 hover:bg-branddark disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {redirecting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> {t("co_redirecting")}
                        </>
                      ) : (
                        <>{payLabel} — {formatAED(total)}</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {customer && step === 3 && orderResult && (
                <div data-testid="offline-success" className="flex flex-col items-center gap-3 py-6 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 14 }}
                  >
                    <ShieldCheck className="h-14 w-14 text-success" />
                  </motion.div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">{t("os_ref")}</p>
                  <p data-testid="offline-order-ref" className="rounded-md border border-brand/60 bg-[#FFFBE0] px-6 py-2.5 font-mono text-lg font-bold tracking-widest text-brandink">
                    {orderResult.order_id}
                  </p>
                  <p className="text-2xl font-extrabold text-brandink">{formatAED(orderResult.total)}</p>
                  <p className="max-w-xs text-xs leading-relaxed text-muted">
                    {orderResult.method === "cod" ? t("os_cod_note") : t("os_bank_note")}
                  </p>
                  {orderResult.bank_details && (
                    <div data-testid="bank-details" className="w-full space-y-1.5 rounded-lg border border-line bg-page p-4 text-start text-xs text-brandink">
                      <p><span className="text-muted">{t("os_bank_name")}:</span> {orderResult.bank_details.bank_name}</p>
                      <p><span className="text-muted">{t("os_bank_account")}:</span> {orderResult.bank_details.account_name}</p>
                      <p>
                        <span className="text-muted">{t("os_bank_iban")}:</span>{" "}
                        <span className="font-mono font-semibold">{orderResult.bank_details.iban}</span>
                      </p>
                    </div>
                  )}
                  <button
                    data-testid="offline-done-button"
                    onClick={close}
                    className="mt-1 rounded-md bg-brand px-10 py-3 text-sm font-bold text-brandink transition-colors duration-200 hover:bg-branddark"
                  >
                    {t("os_done")}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
